import { useState } from 'react';
import { Alert, Badge, Box, Button, Heading, TextareaInput } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { formatWhen } from './evidence-shared.jsx';
import { fieldLabel } from './history.jsx';

/**
 * What a proposal would change: each field, the value it holds now, and the
 * value proposed. The value it holds now is the one the author was looking at —
 * the control is locked while a proposal waits, so it cannot have moved.
 */
export function ProposedChanges({ changes }) {
  return (
    <Box gap="md">
      {changes.map((change) => (
        <Box key={change.field} gap="2xs">
          <Box as="span" fontSize="sm" fontWeight="medium">{fieldLabel(change.field)}</Box>
          <Box as="div" fontSize="sm" color="secondary" style={{ whiteSpace: 'pre-wrap' }}>
            <Value value={change.before} />
          </Box>
          <Box as="div" fontSize="sm" style={{ whiteSpace: 'pre-wrap' }}>
            <Value value={change.after} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function Value({ value }) {
  if (value === null || value === undefined || value === '') {
    return <Box as="span" color="tertiary">—</Box>;
  }
  return <>{value}</>;
}

/**
 * The proposal waiting on this control, at the top of it because nothing else
 * about the control can change until it is decided.
 */
export function PendingProposal({ proposal, canEdit, onDecided, onError }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  async function decide(path, body) {
    setBusy(true);
    onError(null);
    try {
      onDecided(await api.post(`/proposals/${proposal.id}/${path}`, body));
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      gap="lg"
      padding="lg"
      radius="md"
      borderWidth="sm"
      borderColor="warning"
      background="warning"
      maxWidth="60rem"
    >
      <Box direction="row" alignItems="center" gap="sm" fontSize="sm">
        <Badge variant="soft" color="warning">awaiting a decision</Badge>
        <Box as="span" fontWeight="medium">{proposal.proposed_by}</Box>
        <Box as="span" color="secondary">{formatWhen(proposal.created_at)}</Box>
      </Box>

      <Box as="p" style={{ whiteSpace: 'pre-wrap' }}>{proposal.note}</Box>

      <ProposedChanges changes={proposal.changes} />

      <Box as="p" fontSize="sm" color="secondary">
        The control cannot be edited until this is decided.
      </Box>

      {rejecting ? (
        <Box gap="sm" maxWidth="36rem">
          <TextareaInput
            id={`reject-${proposal.id}`}
            label="Why not? (optional)"
            name="note"
            rows={2}
            value={note}
            isDisabled={busy}
            onChange={(event) => setNote(event.target.value)}
          />
          <Box direction="row" gap="sm">
            <Button size="sm" variant="danger" isLoading={busy}
                    onClick={() => decide('reject', { note })}>
              Reject
            </Button>
            <Button size="sm" variant="secondary" isDisabled={busy} onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box direction="row" gap="sm">
          {canEdit && (
            <>
              <Button size="sm" isLoading={busy} onClick={() => decide('approve')}>Approve</Button>
              <Button size="sm" variant="secondary" isDisabled={busy} onClick={() => setRejecting(true)}>
                Reject
              </Button>
            </>
          )}
          {proposal.mine && (
            <Button size="sm" variant="secondary" isLoading={busy} onClick={() => decide('withdraw')}>
              Withdraw
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * What a collaborator has changed but cannot apply, and the note that goes with
 * it. A batch: several fields usually change for one reason, and the reason is
 * most of what an approver needs.
 */
export function SubmitProposal({ haiID, changes, onSubmitted, onDiscard }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fields = Object.keys(changes);
  if (fields.length === 0) return null;

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSubmitted(await api.post(`/controls/${encodeURIComponent(haiID)}/proposals`, { note, changes }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      as="form"
      onSubmit={submit}
      gap="lg"
      padding="lg"
      radius="md"
      borderWidth="sm"
      borderColor="subtle"
      background="secondary"
      maxWidth="60rem"
    >
      <Heading as="h2" size="sm">
        {fields.length === 1 ? '1 change to propose' : `${fields.length} changes to propose`}
      </Heading>

      {error && <Alert variant="warning">{error}</Alert>}

      <Box gap="xs" fontSize="sm">
        {fields.map((field) => <Box key={field}>{fieldLabel(field)}</Box>)}
      </Box>

      <TextareaInput
        id={`proposal-note-${haiID}`}
        label="Why"
        helpText="An editor reads this when deciding."
        name="note"
        rows={3}
        value={note}
        isDisabled={busy}
        onChange={(event) => setNote(event.target.value)}
      />

      <Box direction="row" gap="sm">
        <Button type="submit" size="sm" isLoading={busy} isDisabled={!note.trim()}>
          Submit for review
        </Button>
        <Button size="sm" variant="secondary" isDisabled={busy} onClick={onDiscard}>
          Discard
        </Button>
      </Box>
    </Box>
  );
}
